import { type ReactiveController, type ReactiveControllerHost, css } from 'lit';

import { FUNCTION_KEY } from './function-keys';

/**
 * The search-and-grid picking shared by the emotes panel and the menu's flag
 * page (flag-picker): the search term, the highlighted result, the keys that
 * drive them, and the search box and grid styles. Each keeps its own markup
 * and chrome (header, close or back button, placement); this owns the part
 * they have in common, so a fix to picking lands in both.
 *
 * Markup contract: the panel renders a `.search-box` holding the `<input>`, and
 * a `.grid` of `.item` elements, each with `data-index` set to its position in
 * `results`, both inside a `.picker` wrapper. It sizes the items with
 * `--picker-item-width` and `--picker-item-height` on its host.
 */

/** A pickable item, as the search sees it. */
export interface Searchable {
  /** Lowercase text the term is looked for in: names, codes, aliases. */
  readonly searchText: string;
  /** Lowercase names that rank a result first when one equals the term, and
   *  second when one starts with it. */
  readonly names: readonly string[];
}

/** Items whose search text contains the term: exact name matches first, then
 *  names starting with it, then the rest in their original order. */
function rankedSearch<T extends Searchable>(items: readonly T[], rawTerm: string): readonly T[] {
  const term = rawTerm.toLowerCase();
  if (!term) return items;
  const exact: T[] = [];
  const prefix: T[] = [];
  const rest: T[] = [];
  for (const item of items) {
    if (!item.searchText.includes(term)) continue;
    if (item.names.includes(term)) exact.push(item);
    else if (item.names.some((name) => name.startsWith(term))) prefix.push(item);
    else rest.push(item);
  }
  return [...exact, ...prefix, ...rest];
}

interface GridPickerOptions<T> {
  /** Every item, in the order shown when nothing is typed. */
  readonly items: readonly T[];
  /** A pick from the keyboard. Pointer picks are the panel's own (they play
   *  the click sound), reading the item with itemFrom. */
  readonly onPick: (item: T) => void;
  readonly onClose: () => void;
  /** The rendered grid, while there is one. */
  readonly grid: () => HTMLElement | undefined;
}

export class GridPicker<T extends Searchable> implements ReactiveController {
  /** What's typed in the search box. */
  term = '';

  /** Index in results of the highlighted item, the one Enter picks. */
  selected = 0;

  private resultsTerm: string | null = null;
  private resultsCache: readonly T[] = [];
  private scrolledTo = 0;

  constructor(
    private readonly host: ReactiveControllerHost,
    private readonly options: GridPickerOptions<T>,
  ) {
    host.addController(this);
  }

  /** The items matching the term, best first. Searched again only when the
   *  term changes, not on every render of the panel. */
  get results(): readonly T[] {
    if (this.resultsTerm !== this.term) {
      this.resultsCache = rankedSearch(this.options.items, this.term);
      this.resultsTerm = this.term;
    }
    return this.resultsCache;
  }

  /** An empty search with the first item highlighted, for a fresh open. Call
   *  it before that render (willUpdate), so the first frame shows it. */
  reset(): void {
    this.term = '';
    this.selected = 0;
    this.scrolledTo = 0;
  }

  /** The result a grid item stands for, from its data-index. */
  itemFrom(target: EventTarget | null): T | undefined {
    if (!(target instanceof HTMLElement)) return undefined;
    return this.results[Number(target.dataset['index'])];
  }

  readonly handleInput = (e: Event): void => {
    if (!(e.target instanceof HTMLInputElement)) return;
    this.term = e.target.value;
    this.selected = 0;
    this.host.requestUpdate();
  };

  /**
   * The search box's keys. Function keys carry on to the game, which cancels
   * their browser defaults (F5 would reload the page) and runs their binds,
   * so the key that opened the panel closes it again. Every other key press
   * stops here, or typing would fly the plane. Key releases aren't stopped at
   * all: each one reaches the game, so a key held down as the panel opened
   * can't stay stuck down.
   */
  readonly handleSearchKeyDown = (e: KeyboardEvent): void => {
    if (FUNCTION_KEY.test(e.code)) return;
    e.stopPropagation();
    switch (e.key) {
      case 'Escape':
        this.options.onClose();
        break;
      case 'Enter': {
        const item = this.results[this.selected];
        if (item !== undefined) this.options.onPick(item);
        break;
      }
      case 'ArrowRight':
        this.move(e, 1);
        break;
      case 'ArrowLeft':
        this.move(e, -1);
        break;
      case 'ArrowDown':
        this.move(e, this.columns());
        break;
      case 'ArrowUp':
        this.move(e, -this.columns());
        break;
    }
  };

  /** Keys on a grid item that has focus (reached with Tab), on the same terms
   *  as the search box. */
  readonly handleItemKeyDown = (e: KeyboardEvent): void => {
    if (FUNCTION_KEY.test(e.code)) return;
    e.stopPropagation();
    if (e.key === 'Enter') {
      const item = this.itemFrom(e.currentTarget);
      if (item !== undefined) this.options.onPick(item);
    } else if (e.key === 'Escape') {
      this.options.onClose();
    }
  };

  /** Keeps the highlighted item in view as the arrow keys move it. */
  hostUpdated(): void {
    if (this.selected === this.scrolledTo) return;
    this.scrolledTo = this.selected;
    this.options.grid()?.children.item(this.selected)?.scrollIntoView({ block: 'nearest' });
  }

  private move(e: KeyboardEvent, step: number): void {
    // The arrows move the highlight, not the text cursor
    e.preventDefault();
    this.selected = Math.max(0, Math.min(this.selected + step, this.results.length - 1));
    this.host.requestUpdate();
  }

  /** Columns in the rendered grid: the track count the browser resolved for
   *  its auto-fill template, so the item width lives only in the CSS. */
  private columns(): number {
    const grid = this.options.grid();
    return grid ? getComputedStyle(grid).gridTemplateColumns.split(' ').length : 1;
  }
}

/** The search box, grid and item styles. Item content (image and label
 *  sizes) stays with each panel. Needs the tokens sheet. */
export const gridPickerStyles = css`
  /* Wraps the search box and grid for :focus-within alone, so it stays out
     of the panel's layout. */
  .picker {
    display: contents;
  }

  /* Dressed like the chat input. */
  .search-box {
    height: 30px;
    background-color: var(--color-bg-chip);
    border-radius: var(--radius-xs);
    margin-bottom: 10px;
  }

  .search-box input {
    width: 100%;
    height: 30px;
    box-sizing: border-box;
    padding-left: 12px;
    border: none;
    border-radius: var(--radius-xs);
    background: transparent;
    color: var(--color-text-link);
    text-shadow: var(--shadow-text);
    /* 16px floor: below it iOS Safari auto-zooms the page on focus (the
       viewport meta also caps zoom globally — belt and suspenders; the flags
       search is where the bug actually bit). */
    font-size: 16px;
    font-family: var(--font-family);
    outline: none;
    -webkit-appearance: none;
    /* The panels turn selection off for their taps; typing needs it back. */
    -webkit-user-select: text;
    user-select: text;
  }

  .search-box input::placeholder {
    color: var(--color-text-muted);
    text-shadow: none;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--picker-item-width), 1fr));
    /* Rows keep their own height in a grid taller than its results. */
    align-content: start;
    gap: 4px;
    overflow-y: auto;
    flex: 1;
  }

  .item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 5px 2px;
    border-radius: var(--radius-sm);
    cursor: pointer;
    transition: background-color 0.1s;
    /* Opening mounts every item at once (276 flags, 144 emotes); this skips
       layout and paint (and so SVG rasterization) for the ones below the fold
       until they scroll into view. The intrinsic size stands in until then,
       and auto keeps each item's real size once it has rendered, so the
       scrollbar stays steady. */
    content-visibility: auto;
    contain-intrinsic-size: auto var(--picker-item-width) auto var(--picker-item-height);
  }

  /* Gated so a tapped item doesn't stay lit on a touch screen. */
  @media (hover: hover) {
    .item:hover {
      background-color: var(--color-bg-hover);
    }
  }

  .item:focus {
    outline: none;
  }

  /* The highlight marks what Enter picks, so it shows only while the search
     box (or an item) has focus. On a touch screen, where the box waits for a
     tap, the first item would otherwise look chosen. */
  .item:focus,
  .picker:focus-within .item.selected {
    background-color: var(--color-bg-hover);
  }

  .item .label {
    color: var(--color-text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: center;
  }

  .no-results {
    text-align: center;
    color: var(--color-text-muted);
    padding: 24px;
    font-size: var(--font-size-sm);
    flex: 1;
  }
`;
